import React, { useState } from 'react';
import Modal from '../Modal';
import { useAuth } from '../../helpers/AuthContext';

export default function ProductQuickSort({
    selectedProducts,
    allProducts,
    onClose,
    onSave,
    channel
}) {
    const { fetch } = useAuth();
    const [selectedSort, setSelectedSort] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedProductsData = allProducts.filter(product =>
        selectedProducts.includes(product.uuid)
    );

    const sortOptions = [
        { value: 'newest-first', label: 'Newest to Oldest (by creation date)' },
        { value: 'oldest-first', label: 'Oldest to Newest (by creation date)' },
        { value: 'name-asc', label: 'Alphabetical A-Z (by name)' },
        { value: 'name-desc', label: 'Alphabetical Z-A (by name)' },
        { value: 'price-low-high', label: 'Price Low to High' },
        { value: 'price-high-low', label: 'Price High to Low' },
        { value: 'orders-most-least', label: 'Most Orders to Least Orders' },
        { value: 'orders-least-most', label: 'Least Orders to Most Orders' }
    ];

    const applySorting = (products, sortType) => {
        const sortedProducts = [...products];

        switch (sortType) {
            case 'newest-first':
                return sortedProducts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            case 'oldest-first':
                return sortedProducts.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            case 'name-asc':
                return sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
            case 'name-desc':
                return sortedProducts.sort((a, b) => b.name.localeCompare(a.name));
            case 'price-low-high':
                return sortedProducts.sort((a, b) => a.price - b.price);
            case 'price-high-low':
                return sortedProducts.sort((a, b) => b.price - a.price);
            case 'orders-most-least':
                return sortedProducts.sort((a, b) => (b.totalOrdersCount || 0) - (a.totalOrdersCount || 0));
            case 'orders-least-most':
                return sortedProducts.sort((a, b) => (a.totalOrdersCount || 0) - (b.totalOrdersCount || 0));
            default:
                return sortedProducts;
        }
    };

    const handleSubmit = async () => {
        if (!selectedSort) {
            onClose();
            return;
        }

        setIsSubmitting(true);
        try {
            // Apply sorting to selected products
            const sortedProducts = applySorting(selectedProductsData, selectedSort);
            
            // Create a mapping for the reorg endpoint
            // The endpoint expects { uuid: ordering } where ordering is the new position
            const mapping = {};
            
            // First, let's get all the non-selected products maintaining their current order
            const nonSelectedProducts = allProducts.filter(product => 
                !selectedProducts.includes(product.uuid)
            );
            
            // Find the first and last positions of selected products in the current order
            const selectedIndices = allProducts
                .map((product, index) => selectedProducts.includes(product.uuid) ? index : -1)
                .filter(index => index !== -1);
            
            const firstSelectedIndex = Math.min(...selectedIndices);
            const lastSelectedIndex = Math.max(...selectedIndices);
            
            // Create a map for quick lookup of original positions
            const originalPositions = {};
            allProducts.forEach((product, index) => {
                originalPositions[product.uuid] = index;
            });
            
            // Create the final order: 
            // - Non-selected products before first selected position
            // - Sorted selected products 
            // - Non-selected products after last selected position
            const finalOrder = [
                ...nonSelectedProducts.filter(product => {
                    return originalPositions[product.uuid] < firstSelectedIndex;
                }),
                ...sortedProducts,
                ...nonSelectedProducts.filter(product => {
                    return originalPositions[product.uuid] > lastSelectedIndex;
                })
            ];
            
            // Create mapping using the same logic as ProductsTable (reverse then assign index + 1)
            [...finalOrder].reverse().forEach((product, index) => {
                mapping[product.uuid] = index + 1;
            });

            const response = await fetch('/account/products/reorg', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(mapping)
            });

            if (response.ok) {
                onSave(`Successfully sorted ${selectedProducts.length} products`);
                onClose();
            } else {
                throw new Error('Failed to sort products');
            }
        } catch (error) {
            console.error('Error sorting products:', error);
            onSave('', 'Failed to sort products. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const modalFooter = (
        <div className="group quick-sort-actions">
            <button
                type="button"
                className="button secondary"
                onClick={onClose}
                disabled={isSubmitting}
            >
                Cancel
            </button>
            <button
                type="submit"
                className="button primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedSort}
            >
                {isSubmitting ? 'Sorting...' : 'Apply Sort'}
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={`Quick Sort ${selectedProducts.length} Products`}
            size="medium"
            footer={modalFooter}
            closeOnOverlayClick={!isSubmitting}
            ariaLabelledBy="quick-sort-title"
            ariaDescribedBy="quick-sort-content"
        >
            <div className="quick-sort-content" id="quick-sort-content">
                <div className="selected-products">
                    <h4>Selected Products:</h4>
                    <ul>
                        {selectedProductsData.map(product => (
                            <li key={product.uuid}>
                                {product.name} ({product.type})
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="sort-options">
                    <h4>Choose Sort Order:</h4>
                    <div className="group">
                        <label htmlFor="sort-select">Sort by:</label>
                        <select
                            id="sort-select"
                            value={selectedSort}
                            onChange={(e) => setSelectedSort(e.target.value)}
                            disabled={isSubmitting}
                        >
                            <option value="">Select a sort option...</option>
                            {sortOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </Modal>
    );
}